import { useMemo, useState } from 'react';
import { Table } from 'antd';
import type { CSSProperties } from 'react';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { COLOR } from '@styles/theme';
import { EXPAND_COLUMN_WIDTH, InventoryTableWrapper } from '../list/inventoryListStyled';
import { ReplenishmentLotSubTable } from './ReplenishmentLotSubTable';
import {
  renderReplenishmentProductCell,
  type ReplenishmentListCellContext,
} from './replenishmentListCells';
import {
  getReplenishmentItemRowKey,
  itemHasLots,
  type ReplenishmentColumnKey,
  type ReplenishmentItem,
} from './replenishmentListTypes';

const NO_COLUMN_DIVIDER: CSSProperties = {
  borderInlineEnd: 'none',
};

const CELL_STYLE: CSSProperties = {
  verticalAlign: 'top',
};

const ACTIONS_CELL_STYLE: CSSProperties = {
  verticalAlign: 'middle',
};

const COLUMN_DEFS: { key: ReplenishmentColumnKey; title: string; width: string }[] = [
  { key: 'description', title: 'Product Description', width: '25%' },
  { key: 'organization', title: 'Organization', width: '22%' },
  { key: 'location', title: 'Location', width: '22%' },
  { key: 'quantity_remaining', title: 'Quantity Remaining', width: '12%' },
  { key: 'actions', title: '', width: '19%' },
];

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500];

export type ReplenishmentListTableProps = {
  data: ReplenishmentItem[];
  loading?: boolean;
  filter: { page: number; limit: number };
  totalData: number;
  pageChanged: (page: number) => void;
  filterChanged: (key: string, value: number) => void;
  isIgnoredList: boolean;
  actionLoading: Record<string, boolean>;
  noDataMessage: string;
  openPopup: (
    item: ReplenishmentItem & { lotIndex: number | string },
    inventoryId?: string,
  ) => void;
  ignoreItem: (item: ReplenishmentItem) => void;
  activateItem: (item: ReplenishmentItem) => void;
};

export function ReplenishmentListTable({
  data,
  loading,
  filter,
  totalData,
  pageChanged,
  filterChanged,
  isIgnoredList,
  actionLoading,
  noDataMessage,
  openPopup,
  ignoreItem,
  activateItem,
}: ReplenishmentListTableProps) {
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const cellCtx: ReplenishmentListCellContext = useMemo(
    () => ({
      isIgnoredList,
      actionLoading,
      openPopup,
      ignoreItem,
      activateItem,
    }),
    [isIgnoredList, actionLoading, openPopup, ignoreItem, activateItem],
  );

  const columns: ColumnsType<ReplenishmentItem> = useMemo(
    () =>
      COLUMN_DEFS.map(col => ({
        key: col.key,
        title: col.title,
        width: col.width,
        onHeaderCell: () => ({
          style: { ...NO_COLUMN_DIVIDER, verticalAlign: 'middle' },
        }),
        onCell: () => ({
          style: col.key === 'actions' ? ACTIONS_CELL_STYLE : CELL_STYLE,
        }),
        render: (_: unknown, record: ReplenishmentItem) =>
          renderReplenishmentProductCell(col.key, record, cellCtx),
      })),
    [cellCtx],
  );

  const pagination: TablePaginationConfig = {
    current: filter.page,
    pageSize: filter.limit,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    showTotal: total => `${total} records`,
    size: 'small',
    onChange: (page, pageSize) => {
      if (pageSize !== filter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };

  return (
    <InventoryTableWrapper>
      <Table<ReplenishmentItem>
        className="inventory-list-table"
        rowKey={getReplenishmentItemRowKey}
        columns={columns}
        dataSource={data}
        loading={loading}
        size="middle"
        tableLayout="fixed"
        pagination={pagination}
        showHeader
        locale={{ emptyText: noDataMessage }}
        expandable={{
          columnTitle: 'Lots',
          columnWidth: EXPAND_COLUMN_WIDTH,
          expandedRowKeys,
          onExpandedRowsChange: keys => setExpandedRowKeys(keys as string[]),
          rowExpandable: record => itemHasLots(record),
          expandedRowRender: record => (
            <ReplenishmentLotSubTable item={record} cellCtx={cellCtx} />
          ),
          expandedRowClassName: () => 'inventory-list-expanded-row',
        }}
        style={{
          background: COLOR.white,
          borderRadius: 6,
          border: `1px solid ${COLOR.border}`,
        }}
      />
    </InventoryTableWrapper>
  );
}
